import {FC} from 'react'
import {SyncHrMasterTask, Status, organizationTypeName} from '../query'
import Loading from 'components/Loading'
import {dateFormat} from 'libs/formatter'
import {Close} from 'jbc-front/components/icons'
import classnames from 'classnames'
import styles from './SyncStatusBanner.scss'

const syncStatusTitle = (task: SyncHrMasterTask) => {
  const organizationTypeStr = organizationTypeName(task.organizationType)
  const getUrlAndFilename = () => {
    const TEMPLATE_DIR_PATH = '/csvs/sync_hr_master'
    const endedDateStr = dateFormat(task.endedAt, 'YYYYMMDD')
    const generatedFilename = `差分リスト（${organizationTypeStr}）_${endedDateStr}.csv`
    switch (task.outcome) {
      case 'diff_created':
        return [task.diffCsv?.url, task.diffCsv?.filename]
      case 'diff_empty':
        return [`${TEMPLATE_DIR_PATH}/diff_empty.csv`, generatedFilename]
      case 'source_empty':
        return [`${TEMPLATE_DIR_PATH}/source_empty.csv`, generatedFilename]
      default:
        return [undefined, undefined]
    }
  }
  switch (task.status) {
    case 'failed':
      return `共通マスタから${organizationTypeStr}のデータ取得でエラーが発生しました。`
    case 'waiting':
    case 'in_progress':
      return `共通マスタから${organizationTypeStr}のデータを取得中です。`
    case 'success': {
      const baseMessage = `共通マスタから${organizationTypeStr}のデータ取得が完了しました。最新の情報を反映させるためにはページを更新してください。`
      const [url, filename] = getUrlAndFilename()

      return (
        <span>
          {baseMessage}
          {url && (
            <>
              <br />
              登録された内容は{' '}
              <a href={url} download={filename} className="u-txt-link" target="_blank" rel="noopener noreferrer">
                こちら
              </a>{' '}
              より確認してください。
            </>
          )}
        </span>
      )
    }
    default:
      return
  }
}

const isLoading = (status: Status) => {
  return status === 'waiting' || status === 'in_progress'
}

interface SyncStatusDatesProps {
  task: SyncHrMasterTask
}

const SyncStatusDates: FC<SyncStatusDatesProps> = ({task}) => {
  const {startedAt, endedAt} = task

  return (
    <div className={styles.dates}>
      <dl>
        <dt>実行日時：</dt>
        <dd>{dateFormat(startedAt, 'LLL')}</dd>
      </dl>
      {endedAt && (
        <dl>
          <dt>完了日時：</dt>
          <dd>{dateFormat(endedAt, 'LLL')}</dd>
        </dl>
      )}
    </div>
  )
}

interface errorListProps {
  errorMessage: string | null
}

const ErrorList: FC<errorListProps> = ({errorMessage}) => {
  const errorList = errorMessage?.split(', ') || []
  return (
    <>
      {errorList.map((error, index) => (
        <li key={index} className={styles.error}>
          {error}
        </li>
      ))}
    </>
  )
}

interface SyncStatusBannerProps {
  task: SyncHrMasterTask
  onClose: () => void
}

export const SyncStatusBanner: FC<SyncStatusBannerProps> = ({task, onClose}) => {
  const status = task.status
  const statusClassMap = {
    waiting: styles.inProgress,
    in_progress: styles.inProgress,
    failed: styles.failed,
    success: styles.success,
  }

  if (task.confirmed) {
    return null
  }

  return !task.confirmed ? (
    <div className={classnames(styles.wrap, statusClassMap[status])}>
      <div className={styles.inner}>
        <div className={styles.content}>
          {isLoading(status) && (
            <div className={styles.loading}>
              <Loading />
            </div>
          )}
          <div className={styles.info}>
            <p className={styles.title}>{syncStatusTitle(task)}</p>
            <SyncStatusDates task={task} />
            <ErrorList errorMessage={task.errorMessage} />
          </div>
        </div>
        {!isLoading(status) && (
          <div className={styles.close}>
            <Close size={20} onClick={onClose} />
          </div>
        )}
      </div>
    </div>
  ) : null
}
